import React, { useState } from 'react';
import WorkflowTabView from '../../../components/WorkflowTabView';
import { Calculator, Flame, Zap, Package, TrendingUp, BookOpen } from 'lucide-react';
import Factors from './Factors';
import Scope1 from './Scope1';
import Scope2 from './Scope2';
import Scope3 from './Scope3';
import KPIs from './KPIs';
import Methodology from './Methodology';
import { useTranslation } from 'react-i18next';

const CalculationWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  
  // Define steps configuration for the shared WorkflowTabView
  const steps = [
    { 
      id: 1, 
      title: t('workflow.calculation.steps.factors.title'), 
      description: t('workflow.calculation.steps.factors.description'), 
      icon: Calculator, 
      component: 'Factors' 
    },
    { 
      id: 2, 
      title: t('workflow.calculation.steps.scope1.title'), 
      description: t('workflow.calculation.steps.scope1.description'), 
      icon: Flame, 
      component: 'Scope1' 
    },
    { 
      id: 3, 
      title: t('workflow.calculation.steps.scope2.title'), 
      description: t('workflow.calculation.steps.scope2.description'), 
      icon: Zap, 
      component: 'Scope2' 
    },
    { 
      id: 4, 
      title: t('workflow.calculation.steps.scope3.title'), 
      description: t('workflow.calculation.steps.scope3.description'), 
      icon: Package, 
      component: 'Scope3' 
    },
    { 
      id: 5, 
      title: t('workflow.calculation.steps.kpis.title'), 
      description: t('workflow.calculation.steps.kpis.description'), 
      icon: TrendingUp, 
      component: 'KPIs' 
    },
    { 
      id: 6, 
      title: t('workflow.calculation.steps.methodology.title'), 
      description: t('workflow.calculation.steps.methodology.description'), 
      icon: BookOpen, 
      component: 'Methodology' 
    },
  ];
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem('calculationWorkflowData');
      const savedStep = localStorage.getItem('calculationWorkflowStep');
      
      if (savedData) {
        return {
          formData: JSON.parse(savedData),
          currentStep: savedStep ? parseInt(savedStep) : 1
        };
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    
    return {
      formData: {
        factors: {},
        scope1: {},
        scope2: {},
        scope3: {},
        kpis: {},
        methodology: {}
      },
      currentStep: 1
    };
  };

  const { formData: initialFormData, currentStep: initialStep } = loadFromStorage();
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState(initialFormData);

  // Save data to localStorage
  const saveToStorage = (newFormData, newStep) => {
    try {
      localStorage.setItem('calculationWorkflowData', JSON.stringify(newFormData));
      localStorage.setItem('calculationWorkflowStep', newStep.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handleStepChange = (stepId) => {
    setCurrentStep(stepId);
    saveToStorage(formData, stepId);
  };

  const handleSubmit = (stepData) => {
    const newFormData = {
      ...formData,
      [getStepKey(currentStep)]: stepData
    };
    setFormData(newFormData);
    saveToStorage(newFormData, currentStep);
    console.log('Step submitted:', currentStep, stepData);
  };

  const handleComplete = () => {
    window.location.href = '/';
  };

  const getStepKey = (step) => {
    switch (step) {
      case 1: return 'factors';
      case 2: return 'scope1';
      case 3: return 'scope2';
      case 4: return 'scope3';
      case 5: return 'kpis';
      case 6: return 'methodology';
      default: return 'factors';
    }
  };

  const renderCurrentStep = () => {
    const currentData = formData[getStepKey(currentStep)];

    switch (currentStep) {
      case 1:
        return (
          <Factors onSubmit={handleSubmit} initialData={currentData} />
        );
      case 2:
        return (
          <Scope1 onSubmit={handleSubmit} initialData={currentData} />
        );
      case 3:
        return (
          <Scope2 onSubmit={handleSubmit} initialData={currentData} />
        );
      case 4:
        return (
          <Scope3 onSubmit={handleSubmit} initialData={currentData} />
        );
      case 5:
        return (
          <KPIs onSubmit={handleSubmit} initialData={currentData} />
        );
      case 6:
        return (
          <Methodology onSubmit={handleSubmit} initialData={currentData} />
        );
      default:
        return <Factors />;
    }
  };

  return (
    <WorkflowTabView
      steps={steps}
      currentStep={currentStep}
      onStepChange={handleStepChange}
      onComplete={currentStep === 6 ? handleComplete : undefined}
      maxWidth="max-w-6xl"
      previewOnly={previewOnly}
    >
      {renderCurrentStep()}
    </WorkflowTabView>
  );
};

export default CalculationWorkflow;
